/**
 * Référentiel fiscal / social Gabon 2026 (CGI · LFI 2026 · CNSS/CNAMGS).
 * Source PO : `REFERENCE_PAIE_GABON_2026_1.xlsx` + fiche technique moteur paie
 * + Décret 0487/PR/MASI 18/12/2025.
 *
 * Réalignement (2026-10) : les taux checklist 2,5 % / 16,5 % (CNSS) et
 * 1,5 % / 4,1 % non plafonné (CNAMGS) sont **anciens** — l’Excel référentiel
 * impose CNSS 5 % / 18 % et CNAMGS 2 % / 4,1 % (plafond 2,5 M).
 *
 * Phase 4 : A4 FGTLS · A6 SMIG 160 k · A9 alias brief · A10 pas de littéraux taux.
 */

import { PARAMETRES_PAIE_COURANTS } from './ParametresPaieDates';

/**
 * CNSS — Décret 0487/PR/MASI 18/12/2025 (Excel référentiel).
 * Base : MIN(brut, PLAFOND_MENSUEL).
 */
export const TAUX_CNSS = {
  SALARIAL: 0.05,
  PATRONAL: 0.18,
  PLAFOND_MENSUEL: 1_500_000,
} as const;

/**
 * CNAMGS — barème 2026 (Excel référentiel + fiche technique).
 * Source : Loi n° 14/2007 + décrets ; barème retenu : 2 % salarial / 4,1 % patronal.
 *
 * Plafond mensuel **2 500 000** (distinct du plafond CNSS 1,5 M).
 * Assiette = MIN(brut − cat2, PLAFOND_CNAMGS).
 */
export const TAUX_CNAMGS = {
  SALARIAL: 0.02,
  PATRONAL: 0.041,
  PLAFOND_MENSUEL: 2_500_000,
} as const;

/**
 * Allocations / prestations familiales (part CNSS patronale) — Phase 2 PAIE-004.
 * **Incluses** dans `TAUX_CNSS.PATRONAL` (18 %), **pas additives**.
 * Ventilation OD / affichage : `calculAF(brut)` ≤ cotisation CNSS patronale.
 */
export const TAUX_AF = {
  PATRONAL: 0.08,
  PLAFOND_MENSUEL: 1_500_000,
} as const;
/** @deprecated Alias audit Phase 2 — utiliser `TAUX_AF.PATRONAL`. */
export const AF_PATRONAL = TAUX_AF.PATRONAL;
/** @deprecated Alias audit Phase 2 — utiliser `TAUX_AF.PLAFOND_MENSUEL`. */
export const AF_PLAFOND = TAUX_AF.PLAFOND_MENSUEL;

/** Ventilation AF (8 % plaf. 1,5 M) — ne pas additionner au total CNSS 18 %. */
export function calculAF(salaireBrut: number): number {
  const brut = Math.max(0, salaireBrut);
  return Math.floor(Math.min(brut, TAUX_AF.PLAFOND_MENSUEL) * TAUX_AF.PATRONAL);
}

/** @deprecated Alias — utiliser TAUX_CNSS.PLAFOND_MENSUEL. */
export const PLAFOND_CNSS = TAUX_CNSS.PLAFOND_MENSUEL;
/**
 * Plafond CNAMGS mensuel (= `TAUX_CNAMGS.PLAFOND_MENSUEL` = 2,5 M).
 * Assiette = MIN(brut − cat2, PLAFOND_CNAMGS). Distinct du plafond CNSS 1,5 M.
 */
export const PLAFOND_CNAMGS = TAUX_CNAMGS.PLAFOND_MENSUEL;

/**
 * Ventilation de la CNSS (décision du dirigeant du 23/10/2026, source officielle : communiqué CNSS du 12 janvier 2026
 * portant application du décret n° 0487/PR/MASI du 18 décembre 2025, taux en vigueur depuis le 1er janvier 2026) :
 *  - prestations familiales et maternité (PF) : 5 % employeur ;
 *  - accidents du travail et maladies professionnelles (AT-MP, « risques professionnels ») : 2 % employeur ;
 *  - assurance vieillesse, invalidité et décès (VID) : 16 % répartis 11 % employeur / 5 % travailleur.
 * Patronal = 5 + 2 + 11 = 18 % (`TAUX_CNSS.PATRONAL`), salarial = 5 % (VID, `TAUX_CNSS.SALARIAL`), total 23 %.
 * Plafond des salaires soumis à cotisations : 1 500 000 FCFA par mois, appliqué à CHAQUE composante (même assiette).
 */
export const TAUX_CNSS_VENTILATION_PATRONALE = {
  /** Prestations familiales et maternité. */
  PF: 0.05,
  /** Accidents du travail et maladies professionnelles. */
  ATMP: 0.02,
  /** Assurance vieillesse, invalidité et décès (part employeur). */
  VID: 0.11,
} as const;

/** Alias rétrocompatible — total CNSS patronale (18 % = PF 5 % + AT-MP 2 % + VID 11 %). */
export const TAUX_CNSS_EMP = TAUX_CNSS.PATRONAL;
/** Prestations familiales et maternité (employeur), 5 % ; stockée sur `cnss_pf_emp`. */
export const TAUX_CNSS_PF_EMP = TAUX_CNSS_VENTILATION_PATRONALE.PF;
/** Accidents du travail et maladies professionnelles (employeur), 2 % ; stockée sur `cnss_rp_emp`. */
export const TAUX_CNSS_RP_EMP = TAUX_CNSS_VENTILATION_PATRONALE.ATMP;
/** Assurance vieillesse, invalidité et décès (employeur), 11 % ; stockée sur `cnss_vid_emp`. */
export const TAUX_CNSS_VID_EMP = TAUX_CNSS_VENTILATION_PATRONALE.VID;
/** @deprecated Alias — utiliser TAUX_CNSS.SALARIAL. */
export const TAUX_CNSS_VID_SAL = TAUX_CNSS.SALARIAL;

/** @deprecated Alias — utiliser TAUX_CNAMGS.PATRONAL. */
export const TAUX_CNAMGS_EMP = TAUX_CNAMGS.PATRONAL;
/** Ancien poste « famille » CNAMGS — non retenu (0 %). */
export const TAUX_CNAMGS_FAM_EMP = 0;
/** @deprecated Alias — utiliser TAUX_CNAMGS.SALARIAL. */
export const TAUX_CNAMGS_SAL = TAUX_CNAMGS.SALARIAL;

/*
 * CFP, FNH, CSS, FGTLS ci-dessous : valeurs « SI ACTIVÉES ». Ces quatre contributions sont
 * DÉSACTIVÉES par défaut (interrupteur `actif` de `contributionsEnAttente` dans la table de
 * paramètres datés) : le référentiel paie v2 ne les contient pas ou laisse le taux « à confirmer ».
 * À valider par l'expert-comptable ; voir `docs/paie/parametres-paie.md`.
 */
/** TFP / CFP — Contribution à la formation professionnelle (patronale), 0,50 % (CGI 2025, LF 2017 art. 9), si activée. */
export const TAUX_CFP = PARAMETRES_PAIE_COURANTS.contributionsEnAttente.cfp.taux;
/**
 * FNH / CDS (patronale) : 2 % sur la même base que la CNSS (CGI 2025 art. 403, texte consolidé le plus récent).
 * Le 3 % de l'ordonnance 15/2024 est antérieur à cette consolidation : non retenu.
 */
export const TAUX_FNH = PARAMETRES_PAIE_COURANTS.contributionsEnAttente.fnh.taux;
/** TCS — Taxe Complémentaire sur Salaires (retenue salariale). */
export const TAUX_TCS = 0.05;

/**
 * SMIG mensuel : 80 000 FCFA (décision du dirigeant 16/10/2026). Valeur lue dans la table
 * de paramètres datés (`ParametresPaieDates.ts`). N'est PLUS le seuil de la TCS ni le seuil CSS.
 */
export const SMIG_MENSUEL_2026 = PARAMETRES_PAIE_COURANTS.smig;
export const SMIG_MENSUEL = SMIG_MENSUEL_2026;
export const SMIG = SMIG_MENSUEL;
/**
 * Revenu mensuel minimum (RMM) : 150 000 FCFA. Intègre le SMIG et les primes obligatoires.
 * Paramètre distinct du SMIG ; contrôle d'alerte sur le revenu mensuel total.
 */
export const RMM_MENSUEL = PARAMETRES_PAIE_COURANTS.rmm;
export const RMM = RMM_MENSUEL;
/**
 * Seuil d'exonération de la TCS (CGI art. 347) : 150 000 FCFA/mois. Règle fiscale propre,
 * volontairement NON liée au SMIG ni au RMM (même si elle coïncide avec le RMM).
 */
export const TCS_SEUIL_MENSUEL = PARAMETRES_PAIE_COURANTS.tcsSeuil;
export const SEUIL_EXONERATION_TCS = TCS_SEUIL_MENSUEL;

/**
 * CSS — Contribution Spéciale de Solidarité (retenue salariale, FISC-001 Phase 2 / FISC-B-07).
 *
 * Règle : **1 %** du brut si brut **>** `CSS_SEUIL` (480 000, gelé).
 * Pratique DGI non confirmée — vérifié 2026-10-14 (Lot B / FISC-B-07).
 */
export const TAUX_CSS = PARAMETRES_PAIE_COURANTS.contributionsEnAttente.css.taux;
/** Alias brief A9. */
export const CSS_TAUX = TAUX_CSS;
/**
 * Seuil CSS : 480 000 FCFA/mois, valeur historique (ancien 3 × SMIG de 160 000).
 * GELÉ : la CSS est une décision du dirigeant en attente (absente du référentiel v2) ; elle ne doit
 * pas suivre le changement de SMIG (3 × 80 000 = 240 000 la ferait payer à bien plus de salariés).
 */
export const CSS_SEUIL = PARAMETRES_PAIE_COURANTS.contributionsEnAttente.css.seuil;

/** `floor(brut × 1 %)` si brut > seuil, sinon 0. */
export function calcCSS(brut: number, taux: number = CSS_TAUX, seuil: number = CSS_SEUIL): number {
  const b = Math.max(0, brut);
  if (b <= seuil) return 0;
  return Math.floor(b * taux);
}

/**
 * FGTLS — Fonds de Garantie des Travailleurs du Secteur Privé (patronal).
 * Loi n° 3/94 du 21/11/1994 : **1 %** sur la masse salariale brute, **sans plafond**.
 * Distinct de la CSS (retenue salariale 1 % au-dessus de 3 × SMIG).
 * Phase 4 A4 — conservé (merge Agent 3 + Agent A4).
 */
export const FGTLS_TAUX = PARAMETRES_PAIE_COURANTS.contributionsEnAttente.fgtls.taux;
/** Alias audit Phase 4 A4 — même valeur que `FGTLS_TAUX`. */
export const TAUX_FGTLS = FGTLS_TAUX;

/** `floor(brut × 1 %)` — assiette = brut, aucun plafond. */
export function calcFGTLS(salaireBrut: number, taux: number = FGTLS_TAUX): number {
  const b = Math.max(0, salaireBrut);
  return Math.floor(b * taux);
}

/**
 * Précompte sur loyers : taux HISTORIQUE (10 %), conservé uniquement pour une opération sans date ou antérieure à la
 * ligne datée `retenue_loyers_178bis` (5 % depuis 2017-01-01, CGI art. 178 bis). `PrecompteISService` lit la table datée.
 */
export const PRECOMPTE_LOYER = 0.1;
/** Retenue à la source sur les non-résidents : 25 % du montant brut hors taxes (CGI 2025 art. 206, LF 2024). */
export const PRECOMPTE_HONORAIRES_NR = 0.25;
/** Précompte de 9,5 % sur les prestataires de service non assujettis à la TVA (CGI 2025 art. 23, LF 2022). */
export const PRECOMPTE_HONORAIRES_RES = 0.095;
export const PRECOMPTE_PRESTATIONS = 0.095;

export const TAUX_TVA = 0.18;
/** Taux réduit CGI Gabon (produits/services spécifiques). */
export const TAUX_TVA_REDUIT = 0.05;
/** Taux intermédiaire CGI Gabon. */
export const TAUX_TVA_INTERMEDIAIRE = 0.1;
/** IS sociétés — droit commun (CGI). */
export const IS_DROIT_COMMUN = 0.3;
/**
 * IS des banques : droit commun 30 % (CGI art. 16). Nom historique conservé : les MINES ne relèvent
 * PAS de ce taux (voir `IS_MINES`). Source : https://dgi.ga/imposition-des-personnes-morales/impot-sur-les-societes/
 */
export const IS_BANQUES_MINES = 0.3;
/** IS secteur pétrolier (CGI art. 16 : 35 %). */
export const IS_PETROLE = 0.35;
/** IS secteur minier (CGI art. 16 : « porté à 35 % pour les entreprises du secteur pétrolier et minier »). */
export const IS_MINES = 0.35;
/**
 * IRCM (ex-IRVM), prélèvement libératoire de l'IRPP — CGI art. 109 et 110, page DGI « IRCM ».
 * Taux de droit commun 20 % (dividendes, créances, dépôts) ; 15 % bons de caisse des banques ;
 * 10 % obligations à échéance d'au moins 5 ans émises au Gabon.
 */
export const IRVM_DIVIDENDES = 0.2;
/** IRCM : intérêts d'obligations à échéance d'au moins 5 ans émises au Gabon (CGI art. 110). */
export const IRVM_OBLIGATIONS = 0.1;
/** IRCM : intérêts des bons de caisse émis par les banques (CGI art. 110). */
export const IRVM_BONS_CAISSE_BANQUES = 0.15;
/** IRCM : autres revenus de capitaux mobiliers (créances, dépôts, cautionnements), droit commun. */
export const IRVM_AUTRES_CAPITAUX = 0.2;

export const RESERVE_LEGALE_TAUX = 0.1;
export const RESERVE_LEGALE_PLAFOND_CAPITAL = 0.2;
export const SEUIL_CONTINUITE_EXPLOITATION = 0.5;

/**
 * Frais professionnels forfaitaires — CGI art. 94 : 20 %, plafond 10 000 000 FCFA/an.
 * Valeurs lues dans la table de paramètres datés ; l'application à la base IRPP est un
 * paramètre ACTIVABLE (défaut : désactivé) à valider par l'expert-comptable.
 */
export const ABATTEMENT_FRAIS_PRO_TAUX = PARAMETRES_PAIE_COURANTS.abattementFraisPro.taux;
export const ABATTEMENT_FRAIS_PRO_PLAFOND =
  PARAMETRES_PAIE_COURANTS.abattementFraisPro.plafondAnnuel;
/** Plafond mensuel dérivé (= 10 000 000 / 12). */
export const ABATTEMENT_FRAIS_PRO_PLAFOND_MENSUEL = Math.floor(ABATTEMENT_FRAIS_PRO_PLAFOND / 12);

/**
 * Plafond mensuel d’exonération de l’indemnité de transport (bulletins Sophie / Dusk).
 * Au-delà : « Prime de Transport CNSS » — dans le brut, assiette CNSS, hors CNAMGS.
 */
export const PLAFOND_TRANSPORT_EXONERE = 35_000;
/**
 * Plafond mensuel d’exonération de l’indemnité d’entretien véhicule (CGI art.91bis).
 * Distinct du transport (35 000) — moteur COMPOSANTES / Sophie FH·TBL.
 */
export const PLAFOND_VEHICULE_EXONERE = 100_000;
/** Exonération logement CGI art.93 : min(40 % × base, plafond mensuel). */
export const PLAFOND_LOGEMENT_EXONERE = 250_000;
export const TAUX_LOGEMENT_EXONERE = 0.4;

/**
 * Alias brief Phase 4 A9 / A10 — noms plats = mêmes valeurs que `TAUX_*`.
 * Ne pas hardcoder 0.05 / 0.18 / 0.02 / 0.041 ailleurs.
 */
export const CNSS_SALARIAL = TAUX_CNSS.SALARIAL;
export const CNSS_PATRONAL = TAUX_CNSS.PATRONAL;
export const CNSS_PLAFOND = TAUX_CNSS.PLAFOND_MENSUEL;
export const CNAMGS_SALARIAL = TAUX_CNAMGS.SALARIAL;
export const CNAMGS_PATRONAL = TAUX_CNAMGS.PATRONAL;
/** Alias brief — même valeur que `TAUX_TCS` (5 %). */
export const TCS_TAUX = TAUX_TCS;

/**
 * Indemnité de précarité CDD : AUCUNE base légale (Code du travail, art. 60 : le CDD prend fin
 * « sans indemnité de rupture, à l'exception de l'indemnité des congés payés »). Taux légal = 0.
 */
export const INDEMNITE_PRECARITE_CDD = 0;
/**
 * Taux proposé quand une convention ou un contrat prévoit expressément une prime de précarité
 * (option paramétrable, désactivée par défaut).
 */
export const INDEMNITE_PRECARITE_CDD_OPTION = 0.07;

/** Durée légale mensuelle (40 h × 52 / 12). Sert à valoriser les heures supplémentaires. */
export const HEURES_MENSUELLES_LEGAL = 173.33;
/** Coefficients par défaut des colonnes de saisie : NON légaux (art. 196 : conventions). */
export const TAUX_HS_110 = 1.1;
export const TAUX_HS_125 = 1.25;
export const TAUX_HS_150 = 1.5;
export const TAUX_HS_200 = 2;
/** Trentièmes : une journée d'absence = 1/30 du salaire de base. */
export const JOURS_MOIS_PAIE = 30;
/**
 * Prime d’ancienneté (barème conventionnel courant, 11 des 13 conventions de la base juridique) :
 * 2 % après 2 ans d'ancienneté, puis +1 % par an (n % à n ans), plafonnée à 30 % (plafond conservé
 * par prudence : seule la convention des Banques l'écrit, à valider). Le Code du travail n'institue
 * aucune prime d'ancienneté légale.
 */
export const TAUX_PRIME_ANCIENNETE = 0.02;
/** Progression annuelle après le premier palier (+1 % par an). */
export const TAUX_PRIME_ANCIENNETE_ANNUEL = 0.01;
export const ANCIENNETE_SEUIL_ANS = 2;
export const ANCIENNETE_PLAFOND = 0.3;

export const SEUIL_DELEGUE_PERSONNEL = 11;
export const SEUIL_CHS = 50;
export const SEUIL_REGLEMENT_INTERIEUR = 20;
export const SEUIL_CAC_OHADA_CA = 1_000_000_000;
export const SEUIL_CAC_OHADA_ACTIF = 500_000_000;
export const SEUIL_CAC_OHADA_EFFECTIF = 250;
